/**
 * RollVideoRecorder — short-clip MediaRecorder wrapper for roll video proof (TICKET-281c).
 *
 * Records short clips from a live MediaStream up to a hard cap, with automatic stop,
 * MIME fallback, and a single retry at half bitrate when the clip exceeds size limits.
 *
 * @module services/rollVideoRecorder
 */

import {
  HARD_CAP_RECORDING_SECONDS,
  ROLL_VIDEO_BITRATE_BPS,
  MAX_ROLL_VIDEO_BYTES,
  ROLL_VIDEO_MIME_CANDIDATES,
  type RollVideoContentType,
} from '@/constants/rollMedia';
import { pickSupportedMimeType } from '@/utils/platform';

const SERVICE_TAG = '[ROLL_VIDEO_RECORDER]';

/** Error codes emitted by the recorder. */
export type RollVideoRecorderErrorCode = 'NO_MIME' | 'OVERSIZE' | 'NO_DATA' | 'ABORTED';

/**
 * Typed error thrown / rejected by the recorder. The `code` discriminator lets
 * callers branch on cause without string-matching messages.
 */
export class RollVideoRecorderError extends Error {
  public readonly code: RollVideoRecorderErrorCode;
  constructor(code: RollVideoRecorderErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = 'RollVideoRecorderError';
  }
}

/** Successful recording payload. */
export interface RollVideoRecording {
  /** Encoded video data. */
  blob: Blob;
  /** Full MIME (e.g. `video/webm;codecs=vp9`). */
  mimeType: string;
  /** Narrowed content-type for upload (`video/webm` or `video/mp4`). */
  contentType: RollVideoContentType;
  /** Recording duration in milliseconds. */
  durationMs: number;
  /**
   * Pre-finalize live still frame captured at settle (TICKET-293).
   *
   * Data URL (`data:image/jpeg;base64,...`) grabbed from the live `<video>`
   * element immediately before `stopNow()` runs. Skips the encoder quality
   * tax that re-decoding the recorded blob incurs, so recognition gets a
   * sharper input than `extractFirstFrame(blob)` can produce. Absent on the
   * hard-cap path (no settle = no live grab) and on capture failures —
   * consumers fall back to `extractFirstFrame`.
   */
  preFinalizedFrame?: string;
}

/**
 * Records a short video clip from a live MediaStream.
 *
 * Lifecycle: `start(stream)` → optional `stop()` (auto-fires after
 * HARD_CAP_RECORDING_SECONDS regardless). `abort()` cancels any pending stop.
 */
export class RollVideoRecorder {
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private mimeType: string | null = null;
  private startedAt = 0;
  private autoStopTimer: ReturnType<typeof setTimeout> | null = null;

  // Pending stop() promise wiring
  private resolveStop: ((rec: RollVideoRecording) => void) | null = null;
  private rejectStop: ((err: RollVideoRecorderError) => void) | null = null;

  /** True between `start()` and the first stop event. */
  isRecording(): boolean {
    return this.recorder?.state === 'recording';
  }

  /** Milliseconds since `start()` was called (clamped to 0 if not started). */
  getElapsedMs(): number {
    if (!this.startedAt) return 0;
    return Date.now() - this.startedAt;
  }

  /**
   * Begin recording from the given stream. Throws `RollVideoRecorderError('NO_MIME')`
   * if no candidate MIME is supported by `MediaRecorder`.
   */
  start(stream: MediaStream): void {
    const mime = pickSupportedMimeType(ROLL_VIDEO_MIME_CANDIDATES);
    if (!mime) {
      throw new RollVideoRecorderError('NO_MIME', 'No supported video MIME type for MediaRecorder');
    }
    this.mimeType = mime;
    this.beginRecorder(stream, mime, ROLL_VIDEO_BITRATE_BPS);
  }

  /**
   * Resolves with the final `RollVideoRecording` when recording ends.
   *
   * This is purely an awaiter — it does NOT force the recorder to stop.
   * The recording finishes via the auto-stop timer (after
   * HARD_CAP_RECORDING_SECONDS). To cancel early, call {@link abort}; to
   * end early with a usable clip, call {@link stopNow}.
   */
  stop(): Promise<RollVideoRecording> {
    return new Promise<RollVideoRecording>((resolve, reject) => {
      this.resolveStop = resolve;
      this.rejectStop = reject;
    });
  }

  /**
   * Force the recorder to stop now (rather than waiting for auto-stop).
   * The promise returned by `stop()` will resolve once finalize completes.
   */
  stopNow(): void {
    if (this.recorder && this.recorder.state === 'recording') {
      try {
        this.recorder.stop();
      } catch (err) {
        this.rejectStop?.(new RollVideoRecorderError(
          'NO_DATA',
          `Failed to stop recorder: ${(err as Error).message}`
        ));
        this.rejectStop = null;
        this.resolveStop = null;
      }
    }
  }

  /**
   * Cancel the recording. Rejects any pending `stop()` with `'ABORTED'`.
   *
   * @param reason — Why the recorder is being torn down. Logged for diagnostics
   *   so a normal-flow cleanup (`'cleanup'` after a successful recognition or
   *   user cancellation) doesn't read as an error in the console. `'unmount'`
   *   covers React effect cleanup; `'no-motion-timeout'` covers the 10s
   *   no-motion discard. Defaults to `'cleanup'` (TICKET-297).
   */
  abort(reason: 'cleanup' | 'unmount' | 'no-motion-timeout' = 'cleanup'): void {
    console.info(`${SERVICE_TAG} Recorder cleanup`, { reason });
    this.clearAutoStopTimer();
    if (this.recorder && this.recorder.state !== 'inactive') {
      try {
        this.recorder.stop();
      } catch {
        // Best-effort
      }
    }
    if (this.rejectStop) {
      this.rejectStop(new RollVideoRecorderError('ABORTED', 'Recording aborted'));
      this.rejectStop = null;
      this.resolveStop = null;
    }
    this.recorder = null;
    this.chunks = [];
  }

  /**
   * Internal: build a new MediaRecorder and wire its events. Used both by
   * `start()` and by the half-bitrate retry path inside `finalize()`.
   */
  private beginRecorder(stream: MediaStream, mime: string, bitrate: number): void {
    this.chunks = [];
    this.startedAt = Date.now();
    const recorder = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: bitrate });
    this.recorder = recorder;

    recorder.ondataavailable = (event: BlobEvent) => {
      if (event.data && event.data.size > 0) {
        this.chunks.push(event.data);
      }
    };

    let finalized = false;
    recorder.onstop = () => {
      // Guard against double-fire: some browsers (and our test fake when stop()
      // is invoked twice) can deliver onstop more than once for the same recorder.
      if (finalized) return;
      finalized = true;
      this.clearAutoStopTimer();
      this.finalize();
    };

    try {
      recorder.start();
    } catch (err) {
      this.recorder = null;
      throw new RollVideoRecorderError('NO_DATA', `MediaRecorder.start failed: ${(err as Error).message}`);
    }

    this.autoStopTimer = setTimeout(() => {
      this.autoStopTimer = null;
      if (this.recorder && this.recorder.state === 'recording') {
        try {
          this.recorder.stop();
        } catch (err) {
          console.warn(`${SERVICE_TAG} Auto-stop failed`, err);
        }
      }
    }, HARD_CAP_RECORDING_SECONDS * 1000);
  }

  /**
   * Internal: finalize chunks → blob.
   *
   * Previously this attempted a silent retry-at-half-bitrate on oversize.
   * For variable-length recordings (TICKET-290) that retry could silently
   * eat another 15s of user time without UI feedback. Reject immediately
   * instead — the page surfaces "video too large, try image capture".
   */
  private finalize(): void {
    const mime = this.mimeType ?? 'video/webm';
    const blob = new Blob(this.chunks, { type: mime });
    const durationMs = Date.now() - this.startedAt;

    if (blob.size === 0) {
      const reject = this.rejectStop;
      this.rejectStop = null;
      this.resolveStop = null;
      reject?.(new RollVideoRecorderError('NO_DATA', 'Recorder produced empty blob'));
      return;
    }

    if (blob.size > MAX_ROLL_VIDEO_BYTES) {
      console.warn(`${SERVICE_TAG} Oversize blob`, {
        bytes: blob.size,
        cap: MAX_ROLL_VIDEO_BYTES,
        durationMs,
        mime
      });
      const reject = this.rejectStop;
      this.rejectStop = null;
      this.resolveStop = null;
      reject?.(new RollVideoRecorderError(
        'OVERSIZE',
        `Video too large: ${blob.size}B (cap ${MAX_ROLL_VIDEO_BYTES}B)`
      ));
      return;
    }

    const contentType: RollVideoContentType =
      mime.startsWith('video/mp4') ? 'video/mp4' : 'video/webm';
    const resolve = this.resolveStop;
    this.resolveStop = null;
    this.rejectStop = null;
    resolve?.({ blob, mimeType: mime, contentType, durationMs });
  }

  private clearAutoStopTimer(): void {
    if (this.autoStopTimer) {
      clearTimeout(this.autoStopTimer);
      this.autoStopTimer = null;
    }
  }
}
